import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

/**
 * Registers a "refresh dynamic data" callback with the parent panel.
 * Config renderers that fetch dynamic data (enums, agents, etc.) call this
 * hook so the panel can show a refresh button. On unmount, the registration
 * is cleared.
 *
 * @param registerDynamicRefresh - Callback from ConfigRendererProps (may be undefined)
 * @param queryKeys - Query key prefixes to invalidate when refresh is triggered
 * @param onRefreshed - Optional hook run after the invalidations settle. Steps
 *   whose sockets are derived from the refreshed data (e.g. the Observe
 *   DataSource Link trigger) use this to re-run socket generation — otherwise
 *   refreshing updates the dropdowns but leaves stale sockets on the node.
 */
export function useRegisterDynamicRefresh(
  registerDynamicRefresh:
    ((refresh: (() => Promise<void>) | null) => void) | undefined,
  queryKeys: QueryKey[],
  onRefreshed?: () => void | Promise<unknown>,
) {
  const queryClient = useQueryClient();

  const stableKeyJson = JSON.stringify(queryKeys);

  // Held in a ref so a fresh callback identity on every render does not
  // re-register (and momentarily clear) the panel's refresh button. Written in
  // an effect, not during render — refresh only fires from a committed click,
  // so the post-commit write is always in time.
  const onRefreshedRef = useRef(onRefreshed);
  // No dep array on purpose: callers pass inline closures, so this syncs after
  // every commit rather than re-running on a dependency that is new each render.
  useEffect(() => {
    onRefreshedRef.current = onRefreshed;
  });

  // The refresh closure is built inside the effect (not render) so reading the
  // latest-callback ref stays outside the render phase. Deps are unchanged from
  // the memoized version, so registration churn is the same as before.
  useEffect(() => {
    if (!registerDynamicRefresh) return;
    const refreshFn = async () => {
      const keys: QueryKey[] = JSON.parse(stableKeyJson);
      await Promise.all(
        keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
      await onRefreshedRef.current?.();
    };
    registerDynamicRefresh(refreshFn);
    return () => registerDynamicRefresh(null);
  }, [registerDynamicRefresh, queryClient, stableKeyJson]);
}
